/**
 * Section 03: the project lifecycle this quarter - a vertical ladder of
 * six milestones (commissioned → invoiced). Each step shows how many
 * projects hit that milestone, split by team, and the connectors carry
 * the average days between steps for the quarter's delivered projects.
 */
import { Activity, CalendarClock, Clock, FileSignature, Flag, Hourglass, MessagesSquare, Receipt, Rocket, Trophy } from "lucide-react";
import { InfoTip } from "./methodology";
import { TEAM_HEX } from "./QuarterCharts";
import { useInView } from "./QuarterExtras";
import { CountUp, YoyBadge } from "./QuarterKpis";

const fmtCount = (v) => Math.round(v).toLocaleString("en-GB");

const STAGES = [
  { id: "commissioned", label: "Commissioned", sub: "the client said yes", Icon: Trophy },
  { id: "contracts", label: "Contracts exchanged", sub: "SOW signed", Icon: FileSignature },
  { id: "briefed", label: "Briefed", sub: "project briefed in", Icon: MessagesSquare },
  { id: "in_field", label: "In field", sub: "fieldwork started", Icon: Rocket },
  { id: "finished", label: "Finished", sub: "fieldwork completed", Icon: Flag },
  { id: "invoiced", label: "Invoiced", sub: "bill sent", Icon: Receipt },
];

const TEAM_ORDER = ["UK", "International", "PSE", "PMS", "Other"];

const NOW_ITEMS = [
  { key: "in_field_now", label: "In field today", sub: "fieldwork running right now", Icon: Activity, accent: "#1B4F8A" },
  { key: "awaiting_contracts", label: "Awaiting contracts", sub: "commissioned, contracts not yet signed", Icon: Hourglass, accent: "#b0782a" },
  { key: "about_to_start", label: "About to start", sub: "contracted or briefed, not yet in field", Icon: CalendarClock, accent: "#2d6a4f" },
];

function NowCard({ item, metric, seen, delay }) {
  const total = metric?.value || 0;
  const teams = TEAM_ORDER.filter((t) => (metric?.breakdown || {})[t]);
  return (
    <div
      className="rounded-xl border border-[#1B4F8A]/10 bg-[#eef4fa] shadow-sm shadow-[#1B4F8A]/5 p-4 q-rise"
      style={{ animationDelay: `${delay}ms`, animationPlayState: seen ? "running" : "paused" }}
      data-testid={`lifecycle-now-${item.key}`}
    >
      <div className="flex items-center gap-3">
        <span
          className="w-9 h-9 shrink-0 rounded-full text-white flex items-center justify-center shadow-sm"
          style={{ backgroundColor: item.accent }}
        >
          <item.Icon className="w-4 h-4" />
        </span>
        <div>
          <p className="font-semibold tracking-tight text-2xl text-slate-900 leading-none">
            <CountUp value={total} />
          </p>
          <p className="text-xs font-medium text-slate-800 mt-0.5">{item.label}</p>
        </div>
      </div>
      <p className="text-[10px] text-stone-400 mt-1.5">{item.sub}</p>
      {teams.length > 0 && (
        <>
          <div
            className="flex h-2.5 rounded-full overflow-hidden bg-stone-100 mt-2.5"
            title={teams.map((t) => `${t}: ${metric.breakdown[t]}`).join(" · ")}
          >
            {teams.map((t, ti) => (
              <div
                key={t}
                className="h-full q-grow"
                style={{
                  width: `${(metric.breakdown[t] / total) * 100}%`,
                  backgroundColor: TEAM_HEX[t] || "#a8a29e",
                  animationDelay: `${delay + 200 + ti * 90}ms`,
                  animationPlayState: seen ? "running" : "paused",
                }}
              />
            ))}
          </div>
          <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1.5">
            {teams.map((t) => (
              <span key={t} className="text-[10px] font-mono text-stone-500">
                <span
                  className="inline-block w-1.5 h-1.5 rounded-full mr-1 align-middle"
                  style={{ backgroundColor: TEAM_HEX[t] || "#a8a29e" }}
                />
                {t} <strong className="text-slate-900">{metric.breakdown[t]}</strong>
              </span>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default function QuarterLifecycle({ data, compare, now, footer }) {
  const [ref, seen] = useInView();
  if (!data?.stages?.length) return null;
  const max = Math.max(...data.stages.map((s) => s.n), 1);
  const gapBySrc = Object.fromEntries((data.gaps || []).map((g) => [g.from, g]));
  const legendTeams = TEAM_ORDER.filter((t) =>
    data.stages.some((s) => (s.teams || {})[t]),
  );

  return (
    <div
      ref={ref}
      className="rounded-2xl border border-stone-200 bg-gradient-to-br from-white to-stone-50 shadow-sm p-5 sm:p-8 q-card q-reveal"
      data-testid="qkpi-lifecycle"
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <p className="text-[10px] font-mono uppercase tracking-widest text-stone-500">
          The project lifecycle · this quarter
        </p>
        <InfoTip id="lifecycle" />
        <span className="flex-1" />
        <div className="flex flex-wrap items-center gap-3">
          {legendTeams.map((t) => (
            <span key={t} className="inline-flex items-center gap-1.5 text-[10px] font-mono text-stone-500">
              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: TEAM_HEX[t] || "#a8a29e" }} />
              {t}
            </span>
          ))}
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-7">
        {STAGES.map((st, i) => {
          const s = data.stages.find((x) => x.id === st.id);
          if (!s) return null;
          const gap = i > 0 ? gapBySrc[STAGES[i - 1].id] : null;
          const teams = TEAM_ORDER.filter((t) => (s.teams || {})[t]);
          const delay = i * 120;
          return (
            <div
              key={st.id}
              className="q-rise"
              style={{ animationDelay: `${delay}ms`, animationPlayState: seen ? "running" : "paused" }}
              data-testid={`lifecycle-stage-${st.id}`}
            >
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 shrink-0 rounded-full bg-[#152b45] text-white flex items-center justify-center shadow-md">
                  <st.Icon className="w-4 h-4" />
                </div>
                <p className="font-semibold tracking-tight text-3xl text-slate-900 leading-none">
                  <CountUp value={s.n} />
                </p>
                <span className="flex-1" />
                {gap?.avg_days != null && (
                  <span
                    className="text-[10px] font-mono text-stone-400 whitespace-nowrap cursor-help"
                    title={`Reached on average ${gap.avg_days} ${gap.avg_days === 1 ? "day" : "days"} after the previous step · from ${gap.n} delivered projects`}
                    data-testid={`lifecycle-gap-${gap.from}`}
                  >
                    <Clock className="w-2.5 h-2.5 inline -mt-0.5 mr-0.5 text-[#2a9d8f]" />
                    <strong className="text-slate-700">+{gap.avg_days}d</strong>
                  </span>
                )}
              </div>
              <p className="text-xs font-medium text-slate-800 mt-2">{st.label}</p>
              <p className="text-[10px] text-stone-400 leading-snug">{st.sub}</p>
              <div className="flex flex-wrap items-center gap-1 mt-1.5">
                <YoyBadge
                  pct={s.yoy_pct}
                  name={compare?.yoy || "the same quarter last year"}
                  curValue={s.n}
                  prevValue={s.yoy_n}
                  format={fmtCount}
                />
                <YoyBadge
                  pct={s.qoq_pct}
                  name={compare?.qoq || "the previous quarter"}
                  curValue={s.n}
                  prevValue={s.qoq_n}
                  format={fmtCount}
                />
                <YoyBadge
                  pct={s.avg3_pct}
                  name={compare?.avg3 || "3-yr avg"}
                  curValue={s.n}
                  prevValue={s.avg3_n}
                  format={fmtCount}
                />
              </div>
              <div
                className="flex h-3 rounded-full overflow-hidden bg-stone-100 mt-2.5"
                title={teams.map((t) => `${t}: ${s.teams[t]}`).join(" · ")}
              >
                {teams.map((t, ti) => (
                  <div
                    key={t}
                    className="h-full q-grow"
                    style={{
                      width: `${(s.teams[t] / max) * 100}%`,
                      backgroundColor: TEAM_HEX[t] || "#a8a29e",
                      animationDelay: `${delay + 220 + ti * 90}ms`,
                      animationPlayState: seen ? "running" : "paused",
                    }}
                  />
                ))}
              </div>
              <div className="flex flex-wrap gap-x-2.5 gap-y-0.5 mt-1.5">
                {teams.map((t) => (
                  <span key={t} className="text-[10px] font-mono text-stone-500">
                    <span
                      className="inline-block w-1.5 h-1.5 rounded-full mr-1 align-middle"
                      style={{ backgroundColor: TEAM_HEX[t] || "#a8a29e" }}
                    />
                    {t} <strong className="text-slate-900">{s.teams[t]}</strong>
                  </span>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {now && NOW_ITEMS.some((it) => now[it.key]?.value > 0) && (
        <div className="mt-7" data-testid="lifecycle-now">
          <p className="text-[10px] font-mono uppercase tracking-widest text-stone-500">
            Right now · as of today
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-3">
            {NOW_ITEMS.map((it, i) => (
              <NowCard key={it.key} item={it} metric={now[it.key]} seen={seen} delay={860 + i * 120} />
            ))}
          </div>
        </div>
      )}

      <p className="text-[10px] text-stone-400 mt-6 pt-3 border-t border-stone-100">
        Each step counts the projects reaching that milestone during the quarter - not one group of
        projects moving through. Day gaps are averaged across the {data.delivered_n} projects
        delivered this quarter.
      </p>
      {footer}
    </div>
  );
}
